<template>
  <label
    class="relative flex cursor-pointer items-center justify-between rounded-sm border p-3"
    :class="
      selected ? 'border-primary/30 bg-primary/10' : 'border-divider bg-white'
    "
  >
    <div class="flex w-full items-center justify-between text-ellipsis">
      <div class="flex min-w-0 items-center space-x-4">
        <lf-checkbox
          name="selected_stips"
          :value="stip.id"
          :model-value="selected"
          :data-cy="`stip-${stip.id}`"
          :disabled="disabled"
          @update:model-value="$emit('stip:checkbox-update', $event)"
        />
        <div v-tooltip="stip.name" :title="stip.name" class="truncate">
          {{ stip.name }}
        </div>
      </div>
      <div v-if="!selectOnly" class="flex items-center space-x-2">
        <div v-if="selected" class="flex items-center space-x-1">
          <button
            v-if="!disabled"
            type="button"
            class="text-primary"
            data-cy="increase-count"
            @click.prevent="decreaseStipCount"
          >
            <icon-base :icon="IconSubtract" view-box="0 -1 20 20" />
          </button>
          <div class="flex min-w-4 justify-center text-headline">
            {{ stip.count }}
          </div>
          <button
            v-if="!disabled"
            type="button"
            class="relative top-0.5 text-primary"
            data-cy="decrease-count"
            @click.prevent="increaseStipCount"
          >
            <icon-base :icon="IconPlus" view-box="20 30 40 20" />
          </button>
        </div>
        <div
          class="relative"
          data-cy="edit-stip-description-button"
          @click.prevent="isDescriptionVisible = !isDescriptionVisible"
        >
          <icon-base
            :icon="IconNotes"
            class="cursor-pointer"
            :class="{ 'text-primary': stip.description }"
            v-tooltip="stip.description || $t('STIPS.MANAGE_DESCRIPTION')"
          />
          <div
            v-if="isDescriptionVisible"
            ref="descriptionContent"
            class="flex w-92 flex-col gap-2 z-100 rounded-sm border border-blue-200 bg-white p-2 shadow-lg"
            data-cy="stip-description-container"
          >
            <lf-textarea
              :placeholder="$t('STIPS.ADD_DESCRIPTION')"
              rows="4"
              :name="`stip-description-${stip.id}`"
              noresize
              :character-limit="TEXT_CHARACTER_LIMIT_MEDIUM"
              :value="editedStip.description"
              :disabled="disabled"
              data-cy="stip-description-text-area"
              @key-released="editedStip.description = $event"
            />
            <modal-buttons
              class="grow justify-end px-0"
              :on-cancel="resetEditedStip"
              :on-save="updateStip"
              :disabled="disabled"
              @click.prevent
            />
          </div>
        </div>
      </div>
    </div>
  </label>
</template>
<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { computed, ref, useTemplateRef, watch } from "vue";

import { TEXT_CHARACTER_LIMIT_MEDIUM } from "@/helpers/constants";
import { useTeleportV2 } from "@/hooks/elements";

import IconNotes from "@/components/icons/IconNotes.vue";
import IconPlus from "@/components/icons/IconPlus.vue";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import IconBase from "@/components/ui/IconBase.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

import type { FieldStip } from "@/models/stips";

const MIN_STIP_COUNT = 1;
const MAX_STIP_COUNT = 255;

const emit = defineEmits<{
  "stip:update": [FieldStip];
  "stip:checkbox-update": [boolean];
}>();

const props = defineProps<{
  stip: FieldStip;
  selected: boolean;
  disabled: boolean;
  selectOnly?: boolean;
}>();

const editedStip = ref({ ...props.stip });
const isDescriptionVisible = ref(false);

const descriptionContent = useTemplateRef<HTMLDivElement | null>(
  "descriptionContent"
);

const descriptionPopoverOffset = computed(() => ({
  right: 0,
  top: 20,
}));

const decreaseStipCount = () => {
  const currentCount = editedStip.value.count ?? 1;

  if (!currentCount || currentCount <= MIN_STIP_COUNT) {
    return;
  }

  editedStip.value.count = currentCount - 1;
  emit("stip:update", editedStip.value);
};

const increaseStipCount = () => {
  const currentCount = editedStip.value.count ?? 1;

  if (!currentCount || currentCount >= MAX_STIP_COUNT) {
    return;
  }

  editedStip.value.count = currentCount + 1;
  emit("stip:update", editedStip.value);
};

const resetEditedStip = () => {
  isDescriptionVisible.value = false;
  editedStip.value = { ...props.stip };
};

const updateStip = () => {
  emit("stip:update", editedStip.value);
  resetEditedStip();
};

watch(
  () => props.stip,
  () => {
    editedStip.value = { ...props.stip };
  }
);

useTeleportV2({
  target: descriptionContent,
  to: "#modals",
  checkForOverflow: true,
  offset: descriptionPopoverOffset,
});

onClickOutside(descriptionContent, () => {
  if (isDescriptionVisible.value) {
    resetEditedStip();
  }
});
</script>
